import { HttpErrorResponse } from '@angular/common/http';
import { QueryList } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  BidAskData,
  BlotterRow,
  DescriptionEntry,
  FieldsToBeEdited,
  GenericLeg,
  KeyNumber,
  KeyValue,
  MedusaCodeAndType,
  MultiHedge,
  Position,
  PremiumStyle,
  Pricing,
  QuotedPremiumSection,
  ResultPerLeg,
  RfqQuote,
  RfqSetup,
  StreamParameters,
  UserSettings
} from '@approot/models';
import { dvPaths, dvSchemas } from '@approot/utilities';
import { RfqMultiHedgeGroupComponent } from '@approot/widgets/rfq/rfq-container/rfq-tab/rfq-multi-hedge-group/rfq-multi-hedge-group.component';
import { AdditionalRfqAttributes } from '@approot/widgets/rfq/rfq-container/rfq-tab/rfq-setup/rfq-setup';
import { Observable } from 'rxjs';
import { RFQDetailsDTO } from './rfq-tab/rfq-tab.service';

export const DcdMarginForOptionPrice = 'dcdMarginForOptionPrice';

export type UserRole =
  dvPaths['/session/userRole']['get']['responses'][200]['content']['application/json']['result'];

export type EventNotification = dvSchemas['EventsNotification'];
export type EventNotificationEvents = EventNotification['events'];
export type EventNotificationResult = {
  result: dvSchemas['EventsNotification'];
};

export type BooleanResponse = { result: boolean };
export type KeyValueResponse = { result?: KeyValue };
export type RequestResponse = {
  requestData: { request: any; timestamp: number };
  response: HttpErrorResponse;
};

export type Provider = dvSchemas['ProviderSummaryDTO'];
export type Account = dvSchemas['AccountSummaryDTO'];
export type RFQTabTitle = { shortName: string; longName: string };
export type LoginResponse = {
  providers?: { result: Provider[] };
  accounts?: { result: Account[] };
  todaysTrades?: { result: BlotterRow[] };
};

export type LoginPayload = {
  user: string;
  password: string;
  entity: string;
  userId?: number;
};
export type MedusaRequestOptions = {
  ignoreError?: boolean;
  logError?: boolean;
  httpOptions?: any;
};

export type QuoteEvent = (
  | dvSchemas['ProviderQuoteEvent']
  | dvSchemas['ClientQuoteEvent']
) & { processed: boolean };

type BaseExecutionContextPayload = {
  task?: 'getExecutionContext';
  products: any[];
};

export type ExecutionContextPayload =
  | BaseExecutionContextPayload
  | (BaseExecutionContextPayload & {
      isProvider: true;
      quoteEvent: dvSchemas['RFQDetailsDTO'];
    });

export type ExecutionContextType =
  | 'volBased'
  | 'premiumBased'
  | 'premiumBasedVanillasWithSpotFeed'
  | 'genericMultiHedge';

export interface ClientQuoteEventV2 {
  legs: GenericLeg[];
  premiumCurrency: string;
  quote: ClientEventQuoteExtendedV2;
}

export type ClientEventQuoteExtendedV2 = Partial<ClientEventQuoteExtended> & {
  legs: GenericLeg[];
  premiumStyle: PremiumStyle;
  bidQuoteReference?: string;
  offerQuoteReference?: string;
};

export type ApiPriceIsFrom = typeof DcdMarginForOptionPrice;

export type ClientQuoteEventExtended = dvSchemas['ClientQuoteEvent'] & {
  quote: dvSchemas['ClientQuoteEvent']['quote'] & {
    bidQuoteReference?: string;
    offerQuoteReference?: string;
    timestamp: number;
    marginSpread: number;
    description: {
      bid: DescriptionEntry[];
      ask: DescriptionEntry[];
    };
    premiumStyle: PremiumStyle;
    premiumBidPipsTerm_: number;
    premiumBidPercentBase_: number;
    premiumBidPercentTerm_: number;
    premiumOfferPipsTerm_: number;
    premiumOfferPercentBase_: number;
    premiumOfferPercentTerm_: number;
    apiPriceIsFrom: ApiPriceIsFrom;
    // Need both for backward compatibility.  "ID" is the latest
    externalId?: string;
    externalID?: string;
  };
  quoteAdjusted?: boolean;
  originalQuote?: dvSchemas['ClientQuoteEvent']['quote'];
  processed?: boolean;
};

export type ClientEventQuoteExtended = ClientQuoteEventExtended['quote'];

export type ExecutionType = {
  shortName: ExecutionContextType;
  longName: string;
};

export type ExecutionContext = {
  enableMultipleHedges: boolean;
  enableCustomHedgeAmount: boolean;
  usePrimeBroker: boolean;
  useBlockTrades: boolean;
  executionTypes: ExecutionType[];
  streamParameters: StreamParameters;
  enableMultipleHedge?: boolean;
  enableSingleMedusaHedge?: boolean;
  enableProviderSelectionproviders?: boolean;
  disableForwardPremium?: boolean;
  providers?: Provider[];
  primeBrokerDTO?: Provider;
  enableDeltaHedgeRounding?: boolean;
  deltaHedgeRounding?: number;
  enablePremiumRounding?: boolean;
  premiumBpRounding?: number;
  useSpotInRfqPanel?: boolean;
  additionalRfqAttributes: AdditionalRfqAttributes[];
  enableCopyQuote: boolean;
  attributeDisplayOrder: string[];
  useSingleProviderDetailedView?: boolean;
  enableTwoWayCustomHedgeRateForNonDx?: boolean;
  disableExecution?: boolean;
  userRfqPreferences?: UserRfqPreferences;
  hideSingleAccount?: boolean;
  disableForwardHedge?: boolean;
};

export interface UserRfqPreferences {
  account: string;
  currencyPair: string;
  email: string;
  providers: Provider[];
  setup: RfqSetup;
  timestamp_str: string;
}

export type Hedge = dvSchemas['RFQDetailsDTO']['hedge'];

export type BidMidAsk = {
  mid: KeyValue;
  bid: KeyValue;
  ask: KeyValue;
};

export type PortfolioResult = {
  forwardPremium?: BidMidAsk;
  forwardDelta?: number;
  spotPremium?: BidMidAsk;
  spotDelta?: number;
  legPremium?: BidMidAsk;
  premiumBySettlementDate?: Record<string, BidMidAsk>;
};

export type Description = {
  bid: DescriptionEntry[];
  ask: DescriptionEntry[];
};

export type BsOption = {
  timestamp: string;
  results: {
    resultPerLeg: BsOptionResultPerLeg[];
    hedgeResults: [
      {
        isDeltaPremiumAdjusted: boolean;
        forwardPremium: BidMidAsk;
        forwardDelta: KeyValue;
      }
    ];
    hedge: {
      productType: string;
      cross: string;
      spot: number;
      position: string;
      notional: number;
      strike: number;
      faceCurrency: string;
      settlementDate: string;
    };
    portfolioResult: PortfolioResult;
  };
  description: Description;
};

export interface BsOptionResultPerLeg {
  isDeltaPremiumAdjusted: boolean;
  spotPremium: BidMidAsk;
  forwardPremium: BidMidAsk;
  legPremium: BidMidAsk;
  spotDeltaInPercent: number;
  forwardDeltaInPercent: number;
  spotDelta: number;
  forwardDelta: number;
  vega: KeyValue;
}

export type Points = Record<'mid' | 'spread' | 'bid' | 'ask', number>;

export type CostAnalysis = {
  forwardCurve: {
    valueDate: string;
    currencyPair: string;
    forwardPoints: Record<string, Points>;
    constructionMethod: string;
    spot: Points;
  };
  spotHedgingCost: number;
  clientDeltas: {
    spotDelta: number;
    forwardDeltas: Record<string, number>;
  };
  costByForwardDate: Record<string, number>;
};

export interface PortfolioResultV2 extends PortfolioResult {
  premiumBySettlementDate: Record<string, BidMidAsk>;
  premiumPaymentDate: string;
  rho: KeyNumber;
  theta: number;
  pricing: Pricing;
}

export interface InitRfqApiCalls {
  marketFixing: MedusaCodeAndType;
  createDtoCall: {
    result: RFQDetailsDTO;
  };
  getClientAccounts: { result: Account[] };
}

export interface VolBasedRfqOptionPriceV2Result extends BsOption {
  calculationTimestamp: string;
  cross: string;
  forwardPointsHedgingCost: number;
  forwardPointsMultiplier: number;
  hedges: any[];
  over: string;
  portfolioResult: PortfolioResultV2;
  resultPerLeg: ResultPerLeg[];
  premiumCurrency: string;
  premiumDate: string;
  premiumValue: 'Spot' | 'Forward';
  premiumStyle: PremiumStyle;
  products: KeyValue[];
  spot: number;
  under: string;
  comment: string;
  costAnalysis: CostAnalysis;
}

export type VolBasedHedgeExecutionPayloadGuiEvent = {
  executedSide: string;
  premiumCurrency: string;
  premiumValue: Hedge;
  requester: string;
  clientShortName: string; // medusa client
  clientEntity: string; // vol based client aka. entity
  isBlockTrade: boolean;
};

export type VolBasedHedgeExecutionPayload = {
  task?: 'handleVolBasedHedgeExecution';
  hedgeMode: string;
  medusaEvent: ClientQuoteEventExtended;
  guiEvent: VolBasedHedgeExecutionPayloadGuiEvent;
  pricingDetails: BsOption;
  tradeBookingParameters?: UserSettings['tradeBookingParameters'];
  mappedProviderList?: dvSchemas['AccountPrimeBrokerAvailableProviders']['mappedProviderList'];
};

export type LastGetEvents = {
  diff: number;
  visibility: DocumentVisibilityState;
  loggedIn: boolean;
};

export type NewVolsHedgeType =
  | 'spot'
  | 'customSpot'
  | 'forward'
  | 'customForward'
  | 'None';

export interface NewVolsPayload {
  task: 'bsOptionSpotPrice' | 'bsOptionForwardPrice';
  applyHedgeNetting: boolean;
  premiumCurrency: string;
  hedge: {
    hedgeType: NewVolsHedgeType;
    hedgeDirection?: 'Buy' | 'Sell';
    hedgeRate?: number;
    hedgeAmount?: number;
  };
  products: KeyValue[];
}

export interface HedgePayload {
  productType: string;
  settlementDate: string;
  strike: number;
  notional: number;
  position: 'Buy' | 'Sell';
  expiryDate?: string;
  impliedSpot?: number;
}

export interface HedgeProduct {
  productType: string;
  cross: string;
  spot: string;
  position: 'Buy' | 'Sell';
  optionType: 'Call' | 'Put';
  notional: number;
  strike: string;
  counterNotional: number;
  faceCurrency: string;
  expiryDate: string;
  settlementDate: string;
  cutOff: string;
  settlementType: 'Physical';
  volBid: number;
  volAsk: number;
  forwardPointsBid: string;
  forwardPointsAsk: string;
  discountFactorCurrency: string;
  discountFactor: number;
  rfqPremiumMargin: number;
  rfqVolMargin: number;
  premiumValueType: 'Spot' | 'Forward';
  premiumSettlementDate: string;
}

export type VolBasedRfqOptionPricePayloadTask =
  | 'volBasedRfqOptionPrice_v2'
  | 'validateVolBasedRfqHedges';

export type HedgeMode = 'Live' | 'None' | 'DX';

export interface VolBasedRfqOptionPricePayload {
  task: VolBasedRfqOptionPricePayloadTask;
  hedgeMode: HedgeMode;
  premiumCurrency: string;
  premiumValue: string;
  premiumDate: string; // YYYY-MM-DD
  premiumStyle?: PremiumStyle;
  internalHedges?: HedgePayload[]; // until new validate API is available
  hedges: HedgePayload[];
  products: HedgeProduct[];
  requestId: number;
  enableDeltaHedgeRounding: boolean;
  deltaHedgeRounding: number;
  enablePremiumRounding: boolean;
  premiumBpRounding: number;
  comment: string;
}

export type MappedProviderList =
  dvSchemas['AccountPrimeBrokerAvailableProviders']['mappedProviderList'];

export type Timer = {
  timer$: Observable<any>;
  interval: any;
  checkPoint: string;
};

export interface IRfqSetup {
  form: FormGroup;
  valid: boolean;
  getHedges: () => { hedges: any };
  getRawHedges: () => any;
  hedgeMode: any;
  multiHedgeGroups: QueryList<RfqMultiHedgeGroupComponent>;
}

export type ValidateMultiHedgeRFQResponse = {
  isAdmissible: boolean;
  msg?: string;
};

export interface IndicativeQuote {
  bidAsk: BidAskData;
  quotedPremiumSection: QuotedPremiumSection;
  status: 'Indicative';
}

export interface IndicativeQuotes {
  bid: {
    loading: boolean;
    quote: RfqQuote | IndicativeQuote;
  };
  offer: {
    loading: boolean;
    quote: RfqQuote | IndicativeQuote;
  };
}

export interface RfqValidationStatus {
  messages: string;
  isError: boolean;
  isWarning: boolean;
}

export interface ValidateRfq {
  response: {
    isRfqValid: boolean;
    isPricingAllowed: boolean;
    isWarning: boolean;
    msg: string;
    warningMessages: string[];
    allowVolMargin?: boolean;
  };
  status: string;
}

export interface LiveSpotResponse {
  spotBid: number;
  spotAsk: number;
  nDecimals: number;
}

export interface RfqBuilderLeg {
  productType: string;
  product: Record<string, any>;
  missingAttributeOnPricing: string;
  fieldsToBeEdited: FieldsToBeEdited;
  childVanillaOption?: Record<string, any>;
}

export interface BreakDownVanillaStrategy {
  response: {
    haveProductsBeenInverted: boolean;
    products: Record<string, any>[][];
  };
}

export type RfqPosition = Position | 'Two-way';

export type ConnectionState =
  | 'INITIAL'
  | 'CONNECTED'
  | 'DISCONNECTED'
  | 'CONNECTING';

export type RfqDetails = {
  _id: string;
  RFQDetailsDTO: Record<string, any>;
  requestId: number;
  ownProviderQuotesRequired: boolean;
  backToBackExecutionRequired: boolean;
  timestamp: number;
  email: string;
  identity: Record<string, any>;
  timestamp_str: string;
  date_str: string;
  is_wl: boolean;
  setup: {
    account: string;
  };
  hydra: {
    legs: GenericLeg[];
    hedges: MultiHedge[];
    setup: RfqSetup;
  };
  clientProvider: {
    name: string;
    shortName: string;
  };
  medusaRequest: RFQDetailsDTO;
  // Not sure this is the right schema.
  // TODO: see if we can remove the extra nesting under "result" key
  medusaResponse: { result: RFQDetailsDTO };
  comment?: string;
};

export interface AdjustQuoteForMarginPayload {
  products: Record<string, any>[];
  clientQuoteEvent: ClientQuoteEventExtended;
  RFQDetailsDTO: RFQDetailsDTO;
}

type BaseRfqTradeCaptureParams = {
  readOnly?: boolean;
  longName: string;
  optional: boolean;
};

type SpecificRfqTradeCaptureParams =
  | {
      shortName: 'lclSales' | 'portfolio' | 'coverageSales';
      type: 'enum';
      values: string[];
    }
  | {
      shortName: 'forceCashSettlement';
      type: 'boolean';
      default: boolean;
    }
  | {
      shortName: 'volumeCredit' | 'volumeCreditCurrency';
      type: 'string';
    }
  | {
      shortName:
        | 'forcedCashSettlementFixingSource'
        | 'forcedCashSettlementCurrency'
        | 'tradeBookingPremiumCurrency'
        | 'tradeBookingPremiumStyle';
      type: 'string';
      enum: string[];
    }
  | {
      shortName: 'confirmationType';
      productTypes: string[];
      type: 'string';
      enum: string[];
    };

export type RfqTradeCaptureParams = BaseRfqTradeCaptureParams &
  SpecificRfqTradeCaptureParams;

export interface MidPriceInfo {
  premiumInPercent: number;
  pips: number;
  premiumAmountMid: number;
  vols: number[];
  currency: string;
  spotMid: string;
}

export type BookingTradeState = 'INITIAL' | 'BOOKING' | 'BOOKED' | 'ERROR';

export type RfqTimelineEvent = {
  '@type': string;
  action: string;
  hedgeBid?: number;
  hedgeOffer?: number;
  hedgeRateBid?: number;
  hedgeRateOffer?: number;
  premiumAmountBid?: number;
  premiumAmountOffer?: number;
  provider?: string;
  timestampISO?: string;
  isHedge?: boolean;
  position?: string;
  premium?: number;
  dealReference?: string;
};

export interface RfqHedgeQuote {
  info: boolean;
  message: {
    externalId: string;
    quote: HedgeQuoteMessage;
    uuid: number;
    requestId: number;
  };
}
export interface HedgeQuoteMessage {
  created: number;
  hedgeType: string;
  hedges: HedgeQuote[];
  quoteDirection: 'sell' | 'buy';
  quoteId: string;
  quoteReqId: string;
  requestId: number;
  executionType?: 'Live' | 'DX';
  marketRates: {
    spotRates: MarketRateData;
    forwardRates: MarketRateData[];
    swapPoints: MarketRateData[];
  };
}

export interface MarketRateData {
  bid: number;
  ask: number;
  deliveryDate: string;
}

export interface HedgeQuote {
  hedgeAmount: number;
  hedgeCurrency: string;
  hedgeDeliveryDate: string | number;
  hedgeDirection: 'Buy' | 'Sell';
  hedgeRate: number;
  hedgeIsDX: boolean;
}
